import { format } from 'date-fns';
import ExcelJS from 'exceljs';
import type { FileDef, SaveOpts } from './types';

const stamp = (name: string) => `${name}-${format(new Date(), 'yyyyMMdd-HHmmss')}.xlsx`;

const makeBook = (sheet: string) => {
  const book = new ExcelJS.Workbook();
  const ws = book.addWorksheet(sheet);
  return { book, ws };
};

const styleHead = (row: ExcelJS.Row) => {
  row.eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDEEF8' } };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } }
    };
  });
};

const download = async (book: ExcelJS.Workbook, name: string) => {
  const buffer = await book.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = stamp(name);
  link.click();
  URL.revokeObjectURL(url);
};

export const resolveNumFmt = (value: number, colNumFmt?: string, defaultNumFmt?: string): string => {
  if (Number.isInteger(value)) {
    return '#,##0';
  }
  return colNumFmt || defaultNumFmt || '#,##0.###############';
};

export const buildExportWorkbook = <T>(rows: T[], file: FileDef<T>, opts?: Pick<SaveOpts<T>, 'numFmt'>) => {
  const { book, ws } = makeBook(file.sheet);

  ws.mergeCells(1, 1, 1, file.cols.length);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = file.title;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  ws.mergeCells(2, 1, 2, file.cols.length);
  const subtitleCell = ws.getCell(2, 1);
  subtitleCell.value = file.subtitle || '';
  subtitleCell.font = { italic: true, size: 11 };
  subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  ws.addRow([]);

  const head = ws.addRow(file.cols.map(col => col.header));
  styleHead(head);

  rows.forEach(row => {
    const values = file.cols.map(col => col.get(row));
    const excelRow = ws.addRow(values);

    values.forEach((value, index) => {
      if (typeof value === 'number') {
        excelRow.getCell(index + 1).numFmt = resolveNumFmt(value, file.cols[index]?.numFmt, opts?.numFmt);
      }
    });
  });

  file.cols.forEach((col, index) => {
    ws.getColumn(index + 1).width = col.width || 18;
  });

  return book;
};

export const save = async <T>(rows: T[] = [], file: FileDef<T>, opts?: Pick<SaveOpts<T>, 'numFmt'>) => {
  await download(buildExportWorkbook(rows, file, opts), file.name);
};
